import { createSchema } from 'react-hook-form-auto'
import gql from 'graphql-tag'

import { createDeleteUpdater, fileFragment } from '../pintor'
import { Ingredient } from './ingredient'
import { valuesAsArray, flattenAmountRange } from './units'
import { useMutation } from '../graphql/notificatingMutation'
import { useSubscription } from "@apollo/client/react";

export const IngredientListSchema = createSchema('ingredientEntry', {
  amount: {
    type: 'amountRange',
    defaultValue: 1
  },
  unit: {
    type: 'select',
    options: valuesAsArray(),
    defaultValue: 'u'
  },
  ref: {
    type: 'search',
    associated: false,
    model: Ingredient
  }
})

export const mealSchema = createSchema('meal', {
  name: {
    type: 'string',
    required: true,
    max: 255
  },
  memo: {
    type: 'string',
    textarea: true
  },
  ingredientList: {
    type: [IngredientListSchema]
  },
  timeline: {
    type: 'timeline'
  },
  imageIds: {
    type: 'images',
    onlyOne: true
  },
  public: {
    type: 'makePublic',
    path: 'meal'
  }
})

export const mealFragment = gql`
  fragment MealFragment on Meal {
    id
    createdAt
    name
    memo
    recipe
    ingredientList {
      amount
      amountMax
      unit
      ingredient {
        ...IngredientFragment
      }
    }
    timeline {
      id
      kind
      start
      duration
      label
      ingredientIds
    }
    images {
      ...FileFragment
    }
    public
    user {
      id
      username
    }
  }

  ${Ingredient.fragment}
  ${fileFragment}
`

const MEALS_GET = gql`
  query MealsList(
    $search: String,
    $first: Int,
    $num: Int,
    $seeEveryones: Boolean
  ) {
    collection: meals(
      search: $search,
      first: $first,
      num: $num,
      seeEveryones: $seeEveryones
    ) {
      ...MealFragment
    }
  }
  ${mealFragment}
`

const MEALS_SUB = gql`
  subscription MealUpdate {
    mealChange {
      ...MealFragment
    }
  }
  ${mealFragment}
`

const MEALS_COUNT = gql`
  query MealCount($search: String, $seeEveryones: Boolean) {
    count: mealsCount(search: $search, seeEveryones: $seeEveryones)
  }
`

const MEALS_MULTI_GET = gql`
  query MealMultiGet($ids: [ID!]) {
    someMeals(ids: $ids) {
      ...MealFragment
    }
  }
  ${mealFragment}
`

const MEAL_GET = gql`
  query MealGet($id: ID!) {
    meal(id: $id) {
      ...MealFragment
    }
  }
  ${mealFragment}
`

const MEAL_UPDATE = gql`
  mutation MealUpdate($input: MealInput!) {
    mealUpdate(input: $input) {
      ...MealFragment
    }
  }
  ${mealFragment}
`

const MEAL_DELETE = gql`
  mutation MealDelete($id: ID!) {
    mealDelete(id: $id)
  }
  ${mealFragment}
`

const MEAL_DELETED_SUB = gql`
  subscription MealDeletedSub {
    mealDeleted
  }
`

const MEAL_RECIPE_UPDATE = gql`
  mutation MealRecipeUpdate($id: Int!, $recipe: String!) {
    mealRecipeUpdate(id: $id, recipe: $recipe) {
      ...MealFragment
    }
  }
  ${mealFragment}
`

const MEAL_TIMELINE_DONE = gql`
  mutation MealTimelineDone($id: ID!, $segmentId: String!, $done: Boolean!) {
    mealTimelineDone(id: $id, segmentId: $segmentId, done: $done)
  }
`

const MEAL_TIMELINE_DONE_SUB = gql`
  subscription MealTimelineDone($id: ID!) {
    mealTimelineDone(id: $id) {
      mealId
      segmentId
      done
    }
  }
`

function formConvert(doc) {
  const { images, ingredientList } = doc

  return {
    ...doc,
    imageIds: images && images.map(image => image.id),
    ingredientList: ingredientList && ingredientList.map(entry => {
      const { ingredient, amount, amountMax, ...restEntry } = entry

      // Entries can point at a missing ingredient (old data);
      // submit filters entries without ref.id
      return {
        ...restEntry,
        amount: { amount, amountMax },
        ref: {
          id: ingredient && ingredient.id,
          type: 'ingredient',
          search: ingredient ? ingredient.name : ''
        }
      }
    })
  }
}

function mutationConvert(doc) {
  let {
    id,
    images,
    imageIds,
    ingredientList,
    timeline,
    __typename,
    createdAt,
    recipe,
    user,
    ...rest
  } = doc

  const useIds = imageIds || images.map(image => image.id)
  ingredientList = ingredientList.map(entry => {
    const { amount, unit, ref } = entry

    return {
      ...flattenAmountRange(amount),
      unit,
      ref
    }
  })
  timeline = (timeline || []).map(({ __typename, ...entry }) => entry)

  return {
    variables: {
      input: {
        ...rest,
        id: parseInt(id),
        imageIds: useIds,
        ingredientList,
        timeline
      }
    }
  }
}

function useUpdate(options = {}) {
  return useMutation(MEAL_UPDATE, options)
}

function useMealDelete(options = {}) {
  return useMutation(MEAL_DELETE, options)
}

const mealDeleter = createDeleteUpdater({
  query: MEALS_GET,
  subscriptionName: 'mealDeleted',
  collectionKey: 'meals',
  typename: 'Meal'
})
function useMealDeleted() {
  const { data, loading } = useSubscription(MEAL_DELETED_SUB, {
    onData: mealDeleter
  })
  const mealDeleted = data && data.mealDeleted

  return [ mealDeleted, loading ]
}

export const Meal = {
  name: 'meal',
  schema: mealSchema,
  search: MEALS_GET,
  fetch: MEALS_GET,
  fetchIds: MEALS_MULTI_GET,
  sub: MEALS_SUB,
  mainSubName: 'mealChange',
  mainSubCollection: 'collection',
  mainStoreKey: 'meals',
  count: MEALS_COUNT,
  get: MEAL_GET,
  update: MEAL_UPDATE,
  delete: MEAL_DELETE,
  useUpdate,
  useMealDelete,
  useMealDeleted,
  updateRecipe: MEAL_RECIPE_UPDATE,
  timelineDone: MEAL_TIMELINE_DONE,
  timelineDoneSub: MEAL_TIMELINE_DONE_SUB,
  fragment: mealFragment,
  formConvert,
  mutationConvert
}
